import { ChangeDetectionStrategy, Component, OnInit, computed, inject, input, signal } from '@angular/core';
import { Color, LegendPosition, NgxChartsModule, ScaleType } from '@swimlane/ngx-charts';
import { AmountResponse, ChartsService, TonResponse, Type } from 'src/app/services/charts.service';
import { Chart, ChartType } from 'src/app/models/chart.model';
import { Observable, catchError, finalize, of } from 'rxjs';
import { LoggingService } from 'src/app/services/logging.service';
import { IconsComponent } from 'src/app/components/icons/icons.component';
import { UtilitiesService } from 'src/app/services/utilities.service';
import { InfoButtonComponent } from 'src/app/components/info-button/info-button.component';

@Component({
  selector: 'app-area',
  standalone: true,
  imports: [NgxChartsModule, IconsComponent, InfoButtonComponent],
  templateUrl: './area.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AreaComponent implements OnInit {
  public investmentProjectId = input<string>('');
  public chartType = input.required<ChartType>();
  public chartFor = input.required<Type>();

  public ngOnInit(): void {
    let observable: Observable<TonResponse | AmountResponse>;

    switch (this.chartType()) {
      case 'carbon':
        this.legendTitle.update(() => `${this.utilitiesService.carbonTitle()} (tons)`);
        observable = this.chartsService.fetchCarbons(this.investmentProjectId(), this.chartFor());
        break;
      case 'assets':
        this.legendTitle.update(() => `${this.utilitiesService.biologicalAssetsTitle()} (USD)`);
        observable = this.chartsService.fetchAssets(this.investmentProjectId(), this.chartFor());
        break;
      case 'money':
        this.legendTitle.update(() => `${this.utilitiesService.moneyDistributedTitle()} (USD)`);
        observable = this.chartsService.fetchMoney(this.investmentProjectId(), this.chartFor());
        break;
      case 'gains':
        this.legendTitle.update(() => `${this.utilitiesService.capitalGainsTitle()} (USD)`);
        observable = this.chartsService.fetchGains(this.investmentProjectId(), this.chartFor());
        break;
      case 'damage':
        this.legendTitle.update(() => `${this.utilitiesService.damageAvoidedTitle()} (USD)`);
        observable = this.chartsService.fetchDamage(this.investmentProjectId(), this.chartFor());
        break;
      case 'income':
        this.legendTitle.update(() => `${this.utilitiesService.landownersIncomeTitle()} (USD)`);
        observable = this.chartsService.fetchIncome(this.investmentProjectId(), this.chartFor());
        break;
      case 'quality':
        this.legendTitle.update(() => `${this.utilitiesService.qualityHectareTitle()} (USD)`);
        observable = this.chartsService.fetchQuality(this.investmentProjectId(), this.chartFor());
        break;
      default:
        throw Error('Type not implemented!');
    }

    observable
      .pipe(
        catchError((error) => {
          this.logger.error({ message: $localize`Fetch ${this.chartType()} graph value error`, error });

          return of({ past: [], future: [] });
        }),
        finalize(() => this.loading.update(() => false)),
      )
      .subscribe((chartResp) => {
        const chartPast: Chart = {
          name: $localize`Completed`,
          series: [],
        };
        for (const item of chartResp.past) {
          let value = '';
          if ('tons' in item) {
            value = (Math.round(item.tons * 100) / 100).toFixed(2);
          } else {
            value = (Math.round(item.amount * 100) / 100).toFixed(2);
          }
          chartPast.series.push({
            name: item.year.toString(),
            value: value,
          });
        }
        const chartFuture: Chart = {
          name: $localize`Projected`,
          series: [],
        };
        for (const item of chartResp.future) {
          let value = '';
          if ('tons' in item) {
            value = (Math.round(item.tons * 100) / 100).toFixed(2);
          } else {
            value = (Math.round(item.amount * 100) / 100).toFixed(2);
          }
          chartFuture.series.push({
            name: item.year.toString(),
            value: value,
          });
        }

        this.results.set([chartPast, chartFuture]);
      });
  }

  public tickFormatting(value: string): string {
    if (['0', '5', '10', '15', '20', '25', '30'].includes(value)) {
      return value;
    }

    return '';
  }

  private readonly chartsService = inject(ChartsService);
  private readonly logger = inject(LoggingService);
  private readonly utilitiesService = inject(UtilitiesService);

  public loading = signal<boolean>(true);
  public results = signal<Chart[]>([]);
  public legend = signal<boolean>(true);
  public legendTitle = signal<string>('');
  public xAxisLabel = signal<string>($localize`Year`);
  public yAxisLabel = computed(() => {
    return this.chartType() === 'carbon' ? $localize`Tons` : $localize`Dollars`;
  });
  public legendPosition = signal<LegendPosition>(LegendPosition.Below);
  public colorScheme = signal<string | Color>({
    name: '',
    group: ScaleType.Time,
    selectable: true,
    domain: ['#A10A28', '#5b8666'],
  });
  public locale = this.utilitiesService.locale;
}
